/**
 * 0.80: Feldtyp „Freigabe“ (Vier-Augen-Prinzip, Paket B). Der Wert entsteht nur über „Freigeben“,
 * „Ablehnen“, „Zurückziehen“ und „Zurücksetzen“ (Server: `POST /cards/:id/approval`) – nie durch
 * direktes Setzen. Regeln, Texte und Prüfungen hier gemeinsam für Server und Browser.
 */

export interface ApprovalConfig {
  /** Wer freigeben darf; beide leer = alle mit Bearbeitungsrecht */
  approvers: { users: string[]; groups: string[] };
  /** Wer die Karte angelegt hat, darf nicht freigeben */
  notCreator: boolean;
  /** Zuständige dürfen nicht freigeben */
  notAssignee: boolean;
  /** Zweite Freigabe (andere Person) ab einem Betrag/Zahlenwert in diesem Feld */
  second: { fieldId: string; above: number } | null;
  /** Freigabe verfällt, wenn sich eines dieser Felder ändert (das Feld der Zweitfreigabe zählt immer dazu) */
  resetFields: string[];
  /** Freigabe verfällt, wenn Dateien an der Karte hinzukommen oder wegfallen (ohne Kommentardateien) */
  resetFiles: boolean;
}

export const DEFAULT_APPROVAL: ApprovalConfig = { approvers: { users: [], groups: [] }, notCreator: true, notAssignee: false, second: null, resetFields: [], resetFiles: true };

export interface ApprovalEntry {
  userId: string;
  at: string;
}
export interface ApprovalValue {
  status: 'approved' | 'partial' | 'rejected';
  /** nötige Freigaben zum Zeitpunkt der letzten Entscheidung */
  need: 1 | 2;
  by: ApprovalEntry[];
  rejected?: (ApprovalEntry & { reason: string }) | null;
}
export type ApprovalStatus = 'open' | 'partial' | 'approved' | 'rejected';
export type ApprovalDecision = 'approve' | 'reject' | 'withdraw' | 'reset';

export function approvalConfig(c: { approval?: ApprovalConfig | null } | null | undefined): ApprovalConfig {
  return { ...DEFAULT_APPROVAL, ...(c?.approval ?? {}) };
}

/** Gespeicherten Wert lesen (alles andere zählt als „offen“) */
export function approvalOf(v: unknown): ApprovalValue | null {
  if (!v || typeof v !== 'object') return null;
  const x = v as ApprovalValue;
  if (!['approved', 'partial', 'rejected'].includes(x.status) || !Array.isArray(x.by)) return null;
  return x;
}

export function approvalStatus(v: unknown): ApprovalStatus {
  return approvalOf(v)?.status ?? 'open';
}

/** Nötige Freigaben: zwei, wenn der Wert im Feld der Zweitfreigabe die Grenze überschreitet */
export function approvalNeed(cfg: ApprovalConfig, custom: Record<string, unknown>): 1 | 2 {
  if (!cfg.second) return 1;
  const n = Number(custom[cfg.second.fieldId]);
  return Number.isFinite(n) && custom[cfg.second.fieldId] !== null && custom[cfg.second.fieldId] !== undefined && n > cfg.second.above ? 2 : 1;
}

/** Felder, deren Änderung die Freigabe verfallen lässt */
export function approvalResetFields(cfg: ApprovalConfig): string[] {
  return [...new Set([...cfg.resetFields, ...(cfg.second ? [cfg.second.fieldId] : [])])];
}

export interface ApprovalActor {
  userId: string;
  /** Bearbeitungsrecht am Board */
  canEdit: boolean;
  isBoardOwner: boolean;
  groups: string[];
}
export interface ApprovalCard {
  createdBy: string | null;
  assigneeIds: string[];
  custom: Record<string, unknown>;
}

/** Warum diese Person (gerade) nicht entscheiden darf – null = darf */
export function approvalProblem(cfg: ApprovalConfig, value: unknown, decision: ApprovalDecision, a: ApprovalActor, card: ApprovalCard): string | null {
  const v = approvalOf(value);
  if (decision === 'reset') return a.isBoardOwner ? (v ? null : 'Es gibt noch keine Entscheidung.') : 'Zurücksetzen dürfen nur Besitzer*innen des Boards.';
  if (decision === 'withdraw') {
    const mine = !!v && (v.by.some((b) => b.userId === a.userId) || v.rejected?.userId === a.userId);
    return mine ? null : 'Du hast hier nichts entschieden, das du zurückziehen könntest.';
  }
  if (!a.canEdit) return 'Dafür brauchst du Bearbeitungsrecht am Board.';
  const { users, groups } = cfg.approvers;
  if ((users.length || groups.length) && !users.includes(a.userId) && !groups.some((g) => a.groups.includes(g))) return 'Du gehörst nicht zu den Personen, die hier freigeben dürfen.';
  if (cfg.notCreator && card.createdBy === a.userId) return 'Du hast die Karte angelegt – eine andere Person muss entscheiden.';
  if (cfg.notAssignee && card.assigneeIds.includes(a.userId)) return 'Du bist zuständig – eine andere Person muss entscheiden.';
  if (decision === 'approve' && v?.status !== 'rejected' && v?.by.some((b) => b.userId === a.userId)) return 'Du hast schon freigegeben – die zweite Freigabe muss eine andere Person geben.';
  if (decision === 'approve' && v?.status === 'approved') return 'Schon freigegeben.';
  return null;
}

/** Neuer Wert nach einer Entscheidung (null = offen). Vorher `approvalProblem` prüfen. */
export function decideApproval(cfg: ApprovalConfig, value: unknown, decision: ApprovalDecision, userId: string, at: string, card: ApprovalCard, reason?: string | null): ApprovalValue | null {
  const v = approvalOf(value);
  const need = approvalNeed(cfg, card.custom);
  if (decision === 'reset') return null;
  if (decision === 'reject') return { status: 'rejected', need, by: [], rejected: { userId, at, reason: (reason ?? '').trim() } };
  if (decision === 'withdraw') {
    if (!v) return null;
    if (v.rejected?.userId === userId) return null;
    const by = v.by.filter((b) => b.userId !== userId);
    return by.length ? { status: by.length >= need ? 'approved' : 'partial', need, by, rejected: null } : null;
  }
  // Freigeben: nach einer Ablehnung beginnt die Runde neu
  const by = [...(v && v.status !== 'rejected' ? v.by : []), { userId, at }];
  return { status: by.length >= need ? 'approved' : 'partial', need, by, rejected: null };
}

const day = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** Kurztext für Karte, Liste, Export */
export function approvalText(value: unknown, people?: Map<string, { name: string }>, opts: { long?: boolean } = {}): string | null {
  const v = approvalOf(value);
  if (!v) return null;
  const who = (u: string) => people?.get(u)?.name ?? 'jemand';
  if (v.status === 'rejected') return opts.long && v.rejected ? `Abgelehnt von ${who(v.rejected.userId)} am ${day(v.rejected.at)}${v.rejected.reason ? `: ${v.rejected.reason}` : ''}` : 'Abgelehnt';
  if (v.status === 'partial') return opts.long ? `${v.by.length} von ${v.need} Freigaben (${v.by.map((b) => who(b.userId)).join(', ')})` : `${v.by.length}/${v.need} freigegeben`;
  return opts.long ? `Freigegeben von ${v.by.map((b) => `${who(b.userId)} am ${day(b.at)}`).join(' und ')}` : 'Freigegeben';
}

export const APPROVAL_DECISION_TEXT: Record<ApprovalDecision, string> = {
  approve: 'freigegeben',
  reject: 'abgelehnt',
  withdraw: 'die eigene Entscheidung zurückgezogen',
  reset: 'zurückgesetzt',
};
